import React from 'react';

import { EmptyFieldValueRenderer } from '@/components/molecules/EmptyFieldValueRenderer';
import { FieldValue } from '@/components/molecules/FieldValue';
import { LocaleContext } from '@/hooks/useTranslator';
import { NumericField, ValueType } from '@/types/fields';
import { Locale } from '@/types/locale';
import { numbersLocale } from '@/utility/numbersLocale';

interface Props {
  readonly value: ValueType;
  readonly field: NumericField<unknown>;
}
export const NumericFieldValueRenderer: React.FC<Props> = (props: Props): React.ReactElement => {
  const { field, value } = props;
  const locale = React.useContext<Locale>(LocaleContext);

  const formatted = React.useMemo((): string | null => {
    if (value === null) {
      return null;
    }

    const numeric = Number(value);
    if (isNaN(numeric)) {
      console.debug(`expected a numeric value, got: ${value} which is not a number`);
      return null;
    }

    const formatter = new Intl.NumberFormat(numbersLocale(locale), {
      minimumFractionDigits: field.precision,
      maximumFractionDigits: field.precision,
      useGrouping: field.precision !== 0,
    });

    return formatter.format(numeric);
  }, [field.precision, locale, value]);

  if (formatted === null) {
    return <EmptyFieldValueRenderer />;
  }

  return <FieldValue value={formatted} />;
};
